import { IAffiliateCap, ISetAffiliateCapPayload } from "@/api/types/offer-access";

export type LimitType = 'conversion' | 'payout' | 'revenue';
export type LimitPeriod = 'daily' | 'monthly';

export const LIMIT_TYPES: LimitType[] = ['conversion', 'payout', 'revenue'];
export const LIMIT_PERIODS: LimitPeriod[] = ['daily', 'monthly'];

export interface IIndividualLimit {
    // Составной id: `${affiliate_id}-${period}-${type}` — одна запись API разворачивается в несколько строк
    id: string;
    affiliate_id: number;
    affiliate_name: string;
    type: LimitType;
    period: LimitPeriod;
    value: number;
}

export interface INewLimit {
    affiliate_id: number;
    affiliate_name: string;
    type: LimitType;
    period: LimitPeriod;
    value: number;
}

export const LIMIT_TYPE_LABEL: Record<LimitType, string> = {
    conversion: 'по конверсиям',
    payout: 'по выплате',
    revenue: 'по доходу',
};

export const PERIOD_LABEL: Record<LimitPeriod, string> = {
    daily: 'в день',
    monthly: 'в месяц',
};

export const LIMIT_TYPE_OPTIONS = (Object.keys(LIMIT_TYPE_LABEL) as LimitType[])
    .map(value => ({ value, label: LIMIT_TYPE_LABEL[value] }));

export const LIMIT_PERIOD_OPTIONS = (Object.keys(PERIOD_LABEL) as LimitPeriod[])
    .map(value => ({ value, label: PERIOD_LABEL[value] }));

export const buildLimitId = (affiliateId: number, period: LimitPeriod, type: LimitType): string =>
    `${affiliateId}-${period}-${type}`;

/**
 * Разворачиваем запись API (все лимиты партнёра сразу) в строки таблицы —
 * по одной на каждый заданный (значение > 0) тип/период.
 */
export const capToRows = (
    cap: IAffiliateCap,
    nameCache?: Record<number, string>,
): IIndividualLimit[] => {
    const rows: IIndividualLimit[] = [];
    LIMIT_PERIODS.forEach((period) => {
        LIMIT_TYPES.forEach((type) => {
            const value = cap[period]?.[type] ?? 0;
            if (value > 0) {
                rows.push({
                    id: buildLimitId(cap.affiliate_id, period, type),
                    affiliate_id: cap.affiliate_id,
                    affiliate_name: cap.affiliate_name ?? nameCache?.[cap.affiliate_id] ?? '',
                    type,
                    period,
                    value,
                });
            }
        });
    });
    return rows;
};

/**
 * Собираем payload POST из всех строк одного партнёра
 * (незаданные типы/периоды остаются нулями).
 */
export const rowsToPayload = (
    offerId: number,
    affiliateId: number,
    rows: IIndividualLimit[],
): ISetAffiliateCapPayload => {
    const payload: ISetAffiliateCapPayload = {
        offer_id: offerId,
        affiliate_id: affiliateId,
        enabled: rows.some(row => row.value > 0),
        daily_conversion: 0,
        daily_payout: 0,
        daily_revenue: 0,
        monthly_conversion: 0,
        monthly_payout: 0,
        monthly_revenue: 0,
    };
    rows.forEach((row) => {
        const key = `${row.period}_${row.type}` as keyof ISetAffiliateCapPayload;
        (payload[key] as number) = row.value;
    });
    return payload;
};
